import { useState, useMemo, type FormEvent } from 'react';
import {
  useRecordingPolicy, useSaveRecordingPolicy, useApplyRecording,
} from '../../lib/api/hooks-extension.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { Switch } from '../../components/ui/Form.js';
import { ApiClientError } from '../../lib/api/client.js';

/**
 * Recording policy, at both levels that exist.
 *
 * The account sets a default and a retention period; each seat can override the
 * mode. The page shows both because the question an operator arrives with is
 * almost never "what is the default" — it is "who is being recorded", and that
 * is only answerable by looking at the seats.
 */

const MODES = [
  { key: 'none', label: 'Not recorded' },
  { key: 'all', label: 'All calls' },
  { key: 'inbound', label: 'Inbound only' },
  { key: 'outbound', label: 'Outbound only' },
  { key: 'on_demand', label: 'On demand (*1)' },
] as const;

const MODE_LABEL = Object.fromEntries(MODES.map((m) => [m.key, m.label]));

export function RecordingTab({ tenantId }: { tenantId: string }) {
  const policy = useRecordingPolicy(tenantId);
  const extensions = useExtensions(tenantId, { limit: 500 });
  const apply = useApplyRecording(tenantId);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState<string>('all');

  const rows = extensions.data?.data ?? [];
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const e of rows) c[e.record_mode] = (c[e.record_mode] ?? 0) + 1;
    return c;
  }, [rows]);
  const recorded = rows.length - (counts['none'] ?? 0);

  const toggle = (id: string) =>
    setSelected((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  return (
    <>
      <div style={{ marginBottom: 14 }}>
        <h2 className="arc-h2">Call recording</h2>
        <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
          Who is recorded, for how long, and whether callers are told.
        </p>
      </div>

      {policy.error && <ErrorBanner error={policy.error} />}
      {apply.error && <ErrorBanner error={apply.error} />}

      {policy.data && <PolicyForm tenantId={tenantId} policy={policy.data} />}

      <div className="arc-card" style={{ marginTop: 14 }}>
        <div className="arc-card-head">
          <h2 className="arc-h2">Extensions</h2>
          <span className="arc-subtle">
            {recorded} of {rows.length} recorded
          </span>
        </div>

        {/* The bulk bar only exists once something is selected. A permanently
            visible "apply to selected" control invites an empty-selection
            click, and starting to record the wrong people is not undoable. */}
        {selected.size > 0 && (
          <div className="arc-toolbar" style={{ borderRadius: 0 }}>
            <strong style={{ fontSize: 12.5 }}>{selected.size} selected</strong>
            <div className="arc-spacer" />
            <select className="arc-input" style={{ maxWidth: 180 }} value={bulkMode}
              aria-label="Recording mode to apply"
              onChange={(e) => setBulkMode(e.target.value)}>
              {MODES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
            <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
              disabled={apply.isPending}
              onClick={() => {
                const verb = bulkMode === 'none' ? 'Stop recording' : 'Start recording';
                if (!confirm(`${verb} on ${selected.size} extension${selected.size === 1 ? '' : 's'}?`)) return;
                apply.mutate(
                  { extension_ids: [...selected], record_mode: bulkMode },
                  { onSuccess: () => setSelected(new Set()) },
                );
              }}>
              {apply.isPending ? 'Applying…' : 'Apply'}
            </button>
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        )}

        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th style={{ width: 34 }}>
                  <input
                    type="checkbox" aria-label="Select all extensions"
                    checked={rows.length > 0 && selected.size === rows.length}
                    ref={(el) => {
                      if (el) el.indeterminate = selected.size > 0 && selected.size < rows.length;
                    }}
                    onChange={(e) =>
                      setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())}
                  />
                </th>
                <th>Ext</th><th>Name</th><th>Recording</th>
                <th className="arc-td-num">Retention</th>
              </tr>
            </thead>
            {extensions.isLoading ? <TableSkeleton cols={5} /> : (
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id} data-selected={selected.has(e.id)}>
                    <td>
                      <input type="checkbox" checked={selected.has(e.id)}
                        aria-label={`Select ${e.number}`}
                        onChange={() => toggle(e.id)} />
                    </td>
                    <td className="arc-mono">{e.number}</td>
                    <td style={{ fontWeight: 550 }}>{e.display_name}</td>
                    <td>
                      <Badge tone={e.record_mode === 'none' ? 'muted' : 'info'}>
                        {MODE_LABEL[e.record_mode] ?? e.record_mode}
                      </Badge>
                    </td>
                    <td className="arc-td-num">
                      {e.record_retention_days
                        ? `${e.record_retention_days} days`
                        : <span className="arc-subtle">account default</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!extensions.isLoading && rows.length === 0 && (
          <Empty icon="extension" title="No extensions" hint="Add an extension first." />
        )}
      </div>
    </>
  );
}

function PolicyForm({ tenantId, policy }: {
  tenantId: string;
  policy: import('@arcnet/contracts').RecordingPolicy;
}) {
  const save = useSaveRecordingPolicy(tenantId);
  const [mode, setMode] = useState(policy.default_mode);
  const [retention, setRetention] = useState(
    policy.retention_days === null ? '' : String(policy.retention_days));
  const [announce, setAnnounce] = useState(policy.announce_mode);
  const [pause, setPause] = useState(policy.allow_pause);
  const [quota, setQuota] = useState(
    policy.storage_quota_mb === null ? '' : String(policy.storage_quota_mb));

  const err = save.error instanceof ApiClientError ? save.error : null;

  return (
    <form
      className="arc-card"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate({
          default_mode: mode,
          retention_days: retention === '' ? null : Number(retention),
          announce_mode: announce,
          allow_pause: pause,
          storage_quota_mb: quota === '' ? null : Number(quota),
        });
      }}
    >
      <div className="arc-card-head">
        <h2 className="arc-h2">Account policy</h2>
        {policy.updated_at && (
          <span className="arc-subtle">
            updated {new Date(policy.updated_at).toLocaleDateString('en-ZA')}
          </span>
        )}
      </div>

      <div className="arc-card-body">
        {err && (
          <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
            <Icon name="warning" size={14} /><span>{err.message}</span>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14 }}>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="rc-mode">Default for new extensions</label>
            <select id="rc-mode" className="arc-input" value={mode}
              onChange={(e) => setMode(e.target.value as typeof mode)}>
              {MODES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
            {/* Stated explicitly because the opposite behaviour -- a default
                that reaches back and changes 200 existing seats -- is exactly
                what an operator fears when touching this field. */}
            <p className="arc-hint">Existing extensions keep their current setting.</p>
          </div>

          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="rc-ret">Retention</label>
            <div className="arc-row" style={{ gap: 7 }}>
              <input id="rc-ret" className="arc-input" inputMode="numeric" style={{ maxWidth: 100 }}
                value={retention} placeholder="—"
                onChange={(e) => setRetention(e.target.value.replace(/[^0-9]/g, ''))} />
              <span className="arc-muted" style={{ fontSize: 12.5 }}>days</span>
            </div>
            <p className="arc-hint">
              {retention === ''
                ? 'Empty means keep indefinitely — POPIA says only as long as necessary.'
                : `Recordings are deleted after ${retention} days.`}
            </p>
          </div>

          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="rc-ann">Announcement</label>
            <select id="rc-ann" className="arc-input" value={announce}
              onChange={(e) => setAnnounce(e.target.value as typeof announce)}>
              <option value="none">No announcement</option>
              <option value="inbound">Inbound calls</option>
              <option value="outbound">Outbound calls</option>
              <option value="both">Both directions</option>
            </select>
            <p className="arc-hint">"This call may be recorded", played before the call connects.</p>
          </div>

          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="rc-quota">Storage cap</label>
            <div className="arc-row" style={{ gap: 7 }}>
              <input id="rc-quota" className="arc-input" inputMode="numeric" style={{ maxWidth: 110 }}
                value={quota} placeholder="—"
                onChange={(e) => setQuota(e.target.value.replace(/[^0-9]/g, ''))} />
              <span className="arc-muted" style={{ fontSize: 12.5 }}>MB</span>
            </div>
            <p className="arc-hint">Empty means uncapped. Storage is billed per GB-month.</p>
          </div>
        </div>

        <div className="arc-row" style={{ marginTop: 14, gap: 8, alignItems: 'flex-start' }}>
          <Switch id="rc-pause" checked={pause} onChange={setPause} />
          <label htmlFor="rc-pause" style={{ fontSize: 12.5 }}>
            Let agents pause recording mid-call
            <span className="arc-muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
              How card details stay out of a recording (PCI DSS 3.4). Every pause is
              written to the call record, so the gap is accounted for.
            </span>
          </label>
        </div>

        {mode !== 'none' && announce === 'none' && (
          <div className="arc-banner arc-banner-warn" style={{ marginTop: 14 }}>
            <Icon name="warning" size={14} />
            <span>
              Recording without an announcement. RICA permits this where one party to the
              call consents, but check that the account's own policy says the same.
            </span>
          </div>
        )}

        <button type="submit" className="arc-btn arc-btn-primary" style={{ marginTop: 14 }}
          disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save policy'}
        </button>
        {save.isSuccess && !save.isPending && (
          <span className="arc-muted" style={{ marginLeft: 10, fontSize: 12.5 }}>Saved.</span>
        )}
      </div>
    </form>
  );
}
